import React from 'react';

import { AFFECTEDFILESDETAILS } from '../../../constants/applicationConstants';
import { listAffectedFilesDetail } from '../Services/AffectedFileDetailService';
import { normalizeAffectedFileDetail } from '../Components/AffectedFileDetailCatalog/affectedFileDetailCatalogUtils';

const FETCH_BATCH_SIZE = 100;
const DEFAULT_SORT = [{ field: 'commit_date', dir: 'desc' }];
const GENERAL_SEARCH_COLUMNS = [
  'bug_id',
  'file_path',
  'rollout',
  'author',
  'action',
];
const SUPPORTED_SEARCH_FIELDS = new Set([
  'bug',
  'file',
  'path',
  'rollout',
  'author',
  'action',
]);
const EXPRESSION_SERVER_FIELD_MAP = {
  bug: 'bug_id',
  file: 'file_path',
  path: 'file_path',
  rollout: 'rollout',
  author: 'author',
  action: 'action',
};
const normalizeSearchTerm = (value) => String(value ?? '').trim();

const mergeUniqueAffectedFileDetails = (currentItems, nextItems) => {
  const merged = new Map();

  currentItems.forEach((item) => {
    merged.set(String(item.key), item);
  });

  nextItems.forEach((item) => {
    merged.set(String(item.key), item);
  });

  return Array.from(merged.values());
};

const parseSearchExpression = (token) => {
  const tokenValue = String(token ?? '').trim();
  const separatorIndex = tokenValue.indexOf(':');

  if (separatorIndex <= 0) {
    return null;
  }

  const field = tokenValue.slice(0, separatorIndex).trim().toLowerCase();
  const value = tokenValue.slice(separatorIndex + 1).trim();

  if (!SUPPORTED_SEARCH_FIELDS.has(field) || !value) {
    return null;
  }

  return {
    field,
    value,
  };
};

const parseSearchQuery = (searchTerm) => {
  const normalizedQuery = normalizeSearchTerm(searchTerm);

  if (!normalizedQuery) {
    return {
      normalizedQuery: '',
      expressions: [],
      mode: 'empty',
    };
  }

  const tokens = normalizedQuery.split(/\s+/);
  const expressions = tokens.map(parseSearchExpression);
  const isAdvancedQuery =
    expressions.length > 0 && expressions.every(Boolean);

  return {
    normalizedQuery,
    expressions: isAdvancedQuery ? expressions : [],
    mode: isAdvancedQuery ? 'advanced' : 'general',
  };
};

const buildLegacyServerSearchFilters = (searchValue) => {
  const normalizedQuery = normalizeSearchTerm(searchValue);

  if (!normalizedQuery) {
    return [];
  }

  return GENERAL_SEARCH_COLUMNS.map((columnName, index) => ({
    ColumnName: columnName,
    Op: 'contains',
    ColumnValue: normalizedQuery,
    ...(index > 0 ? { Join: 'or' } : {}),
  }));
};

const buildExpressionServerFilters = (expressions = []) =>
  expressions
    .map((expression, index) => {
      const columnName = EXPRESSION_SERVER_FIELD_MAP[expression.field];
      const columnValue = normalizeSearchTerm(expression.value);

      if (!columnName || !columnValue) {
        return null;
      }

      return {
        ColumnName: columnName,
        Op: 'contains',
        ColumnValue: columnValue,
        ...(index > 0 ? { Join: 'and' } : {}),
      };
    })
    .filter(Boolean);

const buildServerSearchFilters = (searchQueryInput) => {
  const searchQuery =
    typeof searchQueryInput === 'string'
      ? parseSearchQuery(searchQueryInput)
      : searchQueryInput;

  if (!searchQuery?.normalizedQuery) {
    return [];
  }

  if (searchQuery.mode !== 'advanced') {
    return buildLegacyServerSearchFilters(searchQuery.normalizedQuery);
  }

  return buildExpressionServerFilters(searchQuery.expressions);
};

const fetchAffectedFileDetailsPage = async ({
  page = 1,
  searchTerm = '',
  filter,
  signal,
} = {}) => {
  const response = await listAffectedFilesDetail({
    limit: FETCH_BATCH_SIZE,
    offset: page,
    filter: Array.isArray(filter) ? filter : buildServerSearchFilters(searchTerm),
    sort: DEFAULT_SORT,
    signal,
    moduleName: AFFECTEDFILESDETAILS,
  });

  if (response?.isSuccess === false && !response?.affectedfiles) {
    throw new Error(
      response?.errorMessage || response?.message || 'Failed to load object change logs.'
    );
  }

  const items = Array.isArray(response?.affectedfiles)
    ? response.affectedfiles.map(normalizeAffectedFileDetail)
    : [];
  const totalFromApi = Number(response?.TotalCount ?? items.length);
  const total = Number.isFinite(totalFromApi) ? totalFromApi : items.length;

  return {
    items,
    total,
  };
};

const useAffectedFileDetailCatalog = ({ searchTerm = '' } = {}) => {
  const [affectedFileDetails, setAffectedFileDetails] = React.useState([]);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [nextPage, setNextPage] = React.useState(2);
  const [refreshing, setRefreshing] = React.useState(false);
  const [reportedTotal, setReportedTotal] = React.useState(0);
  const affectedFileDetailsRef = React.useRef([]);
  const hasLoadedRef = React.useRef(false);
  const requestIdRef = React.useRef(0);
  const activeControllerRef = React.useRef(null);
  const searchQuery = React.useMemo(
    () => parseSearchQuery(searchTerm),
    [searchTerm]
  );
  const serverSearchFilters = React.useMemo(
    () => buildServerSearchFilters(searchQuery),
    [searchQuery]
  );

  const loadPage = React.useCallback(
    async ({ page = 1, append = false, refresh = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      activeControllerRef.current?.abort();
      const requestController = new AbortController();
      activeControllerRef.current = requestController;

      if (refresh) {
        setRefreshing(true);
      } else if (append) {
        setLoadingMore(true);
      } else if (hasLoadedRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setErrorMessage('');

      try {
        const response = await fetchAffectedFileDetailsPage({
          page,
          searchTerm,
          filter: serverSearchFilters,
          signal: requestController.signal,
        });

        if (requestId !== requestIdRef.current) {
          return null;
        }

        const updatedItems = append
          ? mergeUniqueAffectedFileDetails(affectedFileDetailsRef.current, response.items)
          : response.items;
        const updatedTotal = response.total || updatedItems.length;
        const nextPageValue = page + 1;
        const hasMoreValue = updatedItems.length < updatedTotal && response.items.length > 0;

        affectedFileDetailsRef.current = updatedItems;
        setAffectedFileDetails(updatedItems);
        setReportedTotal(updatedTotal);
        setNextPage(nextPageValue);
        setHasMore(hasMoreValue);
        hasLoadedRef.current = true;

        return {
          affectedFileDetails: updatedItems,
          reportedTotal: updatedTotal,
          nextPage: nextPageValue,
          hasMore: hasMoreValue,
        };
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return null;
        }

        if (error?.name === 'AbortError') {
          return null;
        }

        if (!append && !hasLoadedRef.current) {
          affectedFileDetailsRef.current = [];
          setAffectedFileDetails([]);
          setReportedTotal(0);
          setNextPage(2);
          setHasMore(false);
        }

        setErrorMessage(error?.message || 'Unable to load object change logs.');
        return null;
      } finally {
        if (requestId === requestIdRef.current) {
          if (activeControllerRef.current === requestController) {
            activeControllerRef.current = null;
          }

          setLoading(false);
          setLoadingMore(false);
          setRefreshing(false);
        }
      }
    },
    [searchTerm, serverSearchFilters]
  );

  React.useEffect(() => {
    loadPage({
      page: 1,
      append: false,
      refresh: false,
    });
  }, [loadPage]);

  React.useEffect(() => {
    affectedFileDetailsRef.current = affectedFileDetails;
  }, [affectedFileDetails]);

  React.useEffect(
    () => () => {
      activeControllerRef.current?.abort();
    },
    []
  );

  const loadMoreAffectedFileDetails = React.useCallback(async () => {
    if (loading || loadingMore || refreshing || !hasMore) {
      return null;
    }

    return loadPage({
      page: nextPage,
      append: true,
    });
  }, [hasMore, loadPage, loading, loadingMore, nextPage, refreshing]);

  const refreshAffectedFileDetails = React.useCallback(
    () =>
      loadPage({
        page: 1,
        append: false,
        refresh: true,
      }),
    [loadPage]
  );

  return {
    affectedFileDetails,
    errorMessage,
    hasMore,
    loading,
    loadingMore,
    nextPage,
    refreshAffectedFileDetails,
    refreshing,
    reportedTotal,
    loadMoreAffectedFileDetails,
  };
};

export default useAffectedFileDetailCatalog;
